import * as glMatrix from 'gl-matrix';
import { GLEngineResources } from './engineResources.js';
import { Selection } from './selection.js';
import { Pipette } from './pipette.js';
import { GLTexture } from './texture.js';
import { UndoManager } from './undo/undoManager.js';
import { AddOperationAction } from './undo/addOperationAction.js';
import { DeleteOperationAction } from './undo/deleteOperationAction.js';
import { Camera } from './scene/camera.js';
import { Layer } from './layer.js';
import { AlphaSelectionFilter } from './operations/filters/alphaSelection.js';
import { Tool } from './operations/tools/tool.js';

export class GLEngine {
    _resources: GLEngineResources;
    _selection: Selection;
    _pipette: Pipette;
    _finalImage: GLTexture;
    _projection: glMatrix.mat4;
    _editing: boolean;
    _layers: any[];
    _activeLayer: any | null;
    _undoManager: UndoManager;
    _position: glMatrix.vec2;
    _setActiveOperationCallbBack: any;
    _decor: any;

    constructor(templates, gl, activeOperationCallBack) {
        var self = this;
        self._resources = new GLEngineResources(templates, gl);
        self._selection = new Selection(self._resources);
        self._pipette = new Pipette(self._resources);
        self._finalImage = GLTexture.createTexture2d(gl, true);

        self._projection = glMatrix.mat4.create();
        glMatrix.mat4.ortho(self._projection, -gl.canvas.width / 2, gl.canvas.width / 2, -gl.canvas.height / 2, gl.canvas.height / 2, 0.01, 1000);

        self._editing = false;

        self._layers = [];
        self._activeLayer = null;
        self._undoManager = new UndoManager();

        self._position = glMatrix.vec2.create();
        self._setActiveOperationCallbBack = activeOperationCallBack;
    }

    _resizeTargets(width, height) {
        var self = this;
        var gl = self._resources.gl;

        self._finalImage.bind();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.generateMipmap(gl.TEXTURE_2D);
        self._finalImage.unbind();
    }

    resize() {
        var self = this;
        self._resources.meshes['image'].model = glMatrix.mat4.create();
        glMatrix.mat4.fromScaling(self._resources.meshes['image'].model, self._resources.viewportScale);
        glMatrix.mat4.invert(self._resources.meshes['image'].modelInverse, self._resources.meshes['image'].model);
        glMatrix.mat4.ortho(self._projection, -1, 1, -1, 1, 1, 2);
    }

    static create(templates, gl, activeOperationCallBack) {
        return new GLEngine(templates, gl, activeOperationCallBack);
    }

    destroy() {
        var self = this;

        for (var i = 0; i < self._layers.length; ++i) {
            self._layers[i].destroy();
        }
        self._resources.destroy();
        self._selection.destroy();
        self._pipette.destroy();
        self._finalImage.destroy();
    }

    get width() {
        return this._resources.width;
    }

    get height() {
        return this._resources.height;
    }

    get editing() {
        return this._editing;
    }

    set editing(value) {
        var self = this;
        self._editing = value;
    }

    get pipette() {
        return this._pipette;
    }

    get selection() {
        return this._selection;
    }

    get activeLayer() {
        return this._activeLayer;
    }

    set activeLayer(value) {
        this._activeLayer = value;
    }

    get activeOperation() {
        var self = this;
        if (!self._activeLayer) {
            return null;
        }

        return self._activeLayer.activeOperation;
    }

    set activeOperation(value) {
        var self = this;
        if (!self._activeLayer) {
            return;
        }

        self._activeLayer.activeOperation = value;
        self._setActiveOperationCallbBack(value);
    }

    addOperation(value) {
        var self = this;
        if (!self._activeLayer) {
            return;
        }

        if (value instanceof Tool) {
            var action = new AddOperationAction(value, self._activeLayer);
            self._undoManager.do(action);
        } else {
            var action = new AddOperationAction(value, self._activeLayer);
            self._undoManager.do(action);
        }
    }

    deleteOperation(value) {
        var self = this;
        if (!self._activeLayer) {
            return;
        }

        if (value instanceof Tool) {
            var action = new DeleteOperationAction(value, self._activeLayer);
            self._undoManager.do(action);
        } else {
            var action = new DeleteOperationAction(value, self._activeLayer);
            self._undoManager.do(action);
        }
    }

    undo() {
        this._undoManager.undo();
    }

    redo() {
        this._undoManager.redo();
    }

    delete() {
        var self = this;
        if (this.selection.hasSelection === false) {
            self._activeLayer._camera = new Camera(true);
            return;
        }

        this.addOperation(new AlphaSelectionFilter(self._resources.gl, self._resources.width, self._resources.height));
    }

    get imageMesh() {
        return this._resources.meshes['image'];
    }

    get layers() {
        return this._layers;
    }

    get mainLayer() {
        var self = this;
        return self._layers[self._layers.length - 1];
    }

    get gl() {
        return this._resources._gl;
    }

    addLayer(image, alpha) {
        var self = this;
        if (self._layers.length === 0) {
            self._resources.setSize(image.naturalWidth, image.naturalHeight);
            self._resizeTargets(image.naturalWidth, image.naturalHeight);
            self._selection.resize();
            self.resize();
        }
        var layer = new Layer(self._resources, image, alpha);
        self._layers.unshift(layer);
        if (!self._activeLayer) {
            self._activeLayer = layer;
        }
    }

    deleteLayer(layer) {
        var self = this;
        var wasMainLayer = self.mainLayer === layer;
        var index = self._layers.indexOf(layer);
        if (layer === self.activeLayer) {
            self.activeLayer = null;
        }
        if (index !== -1) {
            self._layers.splice(index, 1);
        }
        if (self._layers.length > 0) {
            self._activeLayer = self._layers[0];
            if (wasMainLayer) {
                var newMainLayer = self.mainLayer;
                self._resources.setSize(newMainLayer.image.naturalWidth, newMainLayer.image.naturalHeight);
                self._resizeTargets(newMainLayer.image.naturalWidth, newMainLayer.image.naturalHeight);
                self._selection.resize();
                self.resize();
            }
        }
    }

    get decor() {
        return this._decor;
    }

    touch() {
        var self = this;
        for (var i = 0; i < self._layers.length; ++i) {
            self._layers[i].touch();
        }
    }

    set position(value) {
        this._position = value;
    }

    render(camera) {
        var self = this;
        if (self.width === 0 || self.height === 0) {
            return;
        }

        var gl = self._resources.gl;
        //combine layers
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);
        gl.clearColor(0, 0, 0, 0);

        gl.clear(gl.COLOR_BUFFER_BIT);
        var nextLayerNeedUpdate = false;

        for (var i = 0; i < self._layers.length; ++i) {
            var layer = self._layers[i];
            if (!layer.enable) {
                continue;
            }

            if (nextLayerNeedUpdate) {
                layer.touch();
            }

            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendEquation(gl.FUNC_ADD);
            var program = self._resources.programs['display'];
            program.use();
            gl.uniformMatrix4fv(program.uniformLocation('projection'), false, self._resources.projection);
            gl.uniformMatrix4fv(program.uniformLocation('lookat'), false, self._resources.lookAt);
            gl.uniformMatrix4fv(program.uniformLocation('pan'), false, layer.camera.pan);
            gl.uniformMatrix4fv(program.uniformLocation('zoom'), false, layer.camera.zoom);
            gl.uniformMatrix4fv(program.uniformLocation('model'), false, layer.model);
            gl.uniform1i(program.uniformLocation('invert'), 1);
            gl.activeTexture(gl.TEXTURE0);
            layer.texture.bind();
            gl.uniform1i(program.uniformLocation('texture'), 0);
            gl.uniform1i(program.uniformLocation('has_alpha_texture'), (layer.alpha !== null ? 1 : 0));
            if (layer.alpha) {
                gl.activeTexture(gl.TEXTURE1);
                layer.alpha.bind();
                gl.uniform1i(program.uniformLocation('alpha_texture'), 1);
            }
            self._resources.meshes['layer'].bind(program);
            self._resources.meshes['layer'].draw();
            self._resources.meshes['layer'].unbind(program);
            layer.texture.unbind();
            if (layer.alpha) {
                layer.alpha.unbind();
            }
            program.unuse();
            gl.disable(gl.BLEND);

            layer.store();
            layer.render(self._selection, self.editing);
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);

        self._finalImage.bind();
        gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0, self._resources.width, self._resources.height, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        self._finalImage.unbind();

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        //display
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        gl.clearColor(0.15686274509, 0.15686274509, 0.15686274509, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);

        if (self._layers.length === 0) {
            return;
        }

        var program = self._resources.programs['display'];
        program.use();
        gl.uniformMatrix4fv(program.uniformLocation('projection'), false, self._projection);
        gl.uniformMatrix4fv(program.uniformLocation('lookat'), false, self._resources.lookAt);
        gl.uniformMatrix4fv(program.uniformLocation('pan'), false, camera.pan);
        gl.uniformMatrix4fv(program.uniformLocation('zoom'), false, camera.zoom);
        gl.uniformMatrix4fv(program.uniformLocation('model'), false, self._resources.meshes['image'].model);
        gl.uniform1i(program.uniformLocation('invert'), 0);
        gl.activeTexture(gl.TEXTURE0);
        self._finalImage.bind();
        gl.uniform1i(program.uniformLocation('texture'), 0);
        self._resources.meshes['image'].bind(program);
        self._resources.meshes['image'].draw();
        self._resources.meshes['image'].unbind(program);
        self._finalImage.unbind();
        program.unuse();

        //draw tool
        if (self.activeOperation instanceof Tool) {
            var program = self._resources.programs['draw_brush'];
            program.use();
            self.activeOperation.position = self._position;
            gl.uniformMatrix4fv(program.uniformLocation('pan_inverse'), false, camera.panInverse);
            gl.uniformMatrix4fv(program.uniformLocation('zoom_inverse'), false, camera.zoomInverse);
            gl.uniformMatrix4fv(program.uniformLocation('model_inverse'), false, self._resources.meshes['image'].modelInverse);
            program.setUniforms(self.activeOperation);
            gl.activeTexture(gl.TEXTURE0);
            self._finalImage.bind();
            gl.uniform1i(program.uniformLocation('texture'), 0);
            gl.uniform2f(program.uniformLocation('texture_size'), self._resources.width, self._resources.height);
            self._resources.meshes['image'].bind(program);
            self._resources.meshes['image'].draw();
            self._resources.meshes['image'].unbind(program);
            self._finalImage.unbind();
            program.unuse();
        }

        //draw selection
        if (self._selection.hasSelection) {
            var program = self._resources.programs['draw_selection'];
            program.use();
            gl.uniformMatrix4fv(program.uniformLocation('pan'), false, camera.pan);
            gl.uniformMatrix4fv(program.uniformLocation('zoom'), false, camera.zoom);
            gl.uniformMatrix4fv(program.uniformLocation('model'), false, self._resources.meshes['image'].model);
            self._selection.bindLine(program);
            self._selection.drawLine();
            self._selection.unbindLine(program);
            program.unuse();
        }
        gl.finish();
    }

    renderSelection() {
        var self = this;
        if (!self._selection.hasSelection) {
            return;
        }

        self._selection.render();
    }

    pick(camera, position) {
        var self = this;
        if (self._layers.length === 0) {
            return;
        }

        self.render(camera);
        return self.pipette.pick(position);
    }

    grabImage() {
        try {
            var self = this;
            var gl = self._resources.gl;
            var pixels = new Uint8Array(self._resources.width * self._resources.height * 4);
            gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
            gl.readPixels(0, 0, self._resources.width, self._resources.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            return new ImageData(Uint8ClampedArray.from(pixels), self._resources.width, self._resources.height);
        } catch {
            alert("Nia Nia Nia, tu n'as pas assez de RAM, une petite sauvegarde va te sauver la vie ! 😉")
            return null;
        }
    }

}
